import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Switch from "react-switch";
import Swal from "sweetalert2";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import { database } from "../../database/firebase";
import MonkeyBackdrop from "../../components/MonkeyBackdrop";
import "./userpage.css";

const DURATION_PRESETS = [
  {
    emoji: "🐢",
    label: "1 min 20 sec",
    sub: "Total time: 80 seconds",
    baseMinutes: 1,
    perPlayerSeconds: 80,
  },
  {
    emoji: "🐇",
    label: "1 min 30 sec",
    sub: "Total time: 90 seconds",
    baseMinutes: 1,
    perPlayerSeconds: 90,
  },
  {
    emoji: "⚡",
    label: "1 min 40 sec",
    sub: "Total time: 100 seconds",
    baseMinutes: 1,
    perPlayerSeconds: 100,
  },
  {
    emoji: "🔥",
    label: "1 min 50 sec",
    sub: "Total time: 110 seconds",
    baseMinutes: 1,
    perPlayerSeconds: 110,
  },
  {
    emoji: "💎",
    label: "2 min 00 sec",
    sub: "Total time: 120 seconds",
    baseMinutes: 2,
    perPlayerSeconds: 120,
  },
  {
    emoji: "⭐",
    label: "2 min 10 sec",
    sub: "Total time: 130 seconds",
    baseMinutes: 2, 
    perPlayerSeconds: 130,
  },
   {
    emoji: "🌟",
    label: "2 min 20 sec",
    sub: "Total time: 140 seconds",
    baseMinutes: 2,
    perPlayerSeconds: 140,
  },
   {
    emoji: "🌟",
    label: "2 min 30 sec",
    sub: "Total time: 150 seconds",
    baseMinutes: 2,
    perPlayerSeconds: 150,
  },
   {
    emoji: "✨",
    label: "2 min 40 sec",
    sub: "Total time: 160 seconds",
    baseMinutes: 2,
    perPlayerSeconds: 160, 
  },
  {
    emoji: "🕐",
    label: "2 min 50 sec",
    sub: "Total time: 170 seconds",
    baseMinutes: 2,
    perPlayerSeconds: 170,
  },
  {
    emoji: "🕑",
    label: "3 min 00 sec",
    sub: "Total time: 180 seconds",
    baseMinutes: 3,
    perPlayerSeconds: 180,
  },
  {
    emoji: "🕒",
    label: "3 min 10 sec",
    sub: "Total time: 190 seconds",
    baseMinutes: 3,
    perPlayerSeconds: 190,
  },
   {
    emoji: "🕓",
    label: "3 min 20 sec",
    sub: "Total time: 200 seconds",
    baseMinutes: 3,
    perPlayerSeconds: 200,
  },
  {
    emoji: "🕔",
    label: "3 min 30 sec",
    sub: "Total time: 210 seconds",
    baseMinutes: 3,
    perPlayerSeconds: 210,
  },
  {
    emoji: "🕕",
    label: "3 min 40 sec",
    sub: "Total time: 220 seconds",
    baseMinutes: 3,
    perPlayerSeconds: 220, 
  },
  {
    emoji: "⏰",
    label: "3 min 50 sec",
    sub: "Total time: 230 seconds",
    baseMinutes: 3,
    perPlayerSeconds: 230,
  },
  {
    emoji: "⏰",
    label: "4 min 00 sec",
    sub: "Total time: 240 seconds",
    baseMinutes: 4,
    perPlayerSeconds: 240,
  },
];

const GAME_MODE_INFO = {
  presenter: {
    emoji: "📽️",
    label: "Presenter",
    sub: "You host the session and control the flow",
  },
  player: {
    emoji: "🎮",
    label: "Player",
    sub: "You take part and submit your truths & lies",
  },
};

const MIN_PLAYERS = 2;
const MAX_PLAYERS = 5;
const PLAYER_MODE_CLICK_LOCK_MS = 5000;
const LANDING_LOADER_MS = 3000;
const LOBBY_COLLECTION = "lobby";
/** Attendee roster + gameMode: lobby/{sessionId}/rooms/{roomId}/userdata/{userId} */
const LOBBY_ROOM_USERDATA_SUBCOLLECTION = "userdata";
const PLAYER_CHARACTER_COUNT = 8;

function characterHashFromUserId(userId) {
  let h = 0;
  const s = String(userId ?? "");
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function characterIndexForUserId(userId) {
  const count =
    Number.isFinite(PLAYER_CHARACTER_COUNT) && PLAYER_CHARACTER_COUNT > 0
      ? PLAYER_CHARACTER_COUNT
      : 8;
  return characterHashFromUserId(userId) % count;
}

function characterFieldsForUser(sessionUser) {
  const userId = String(sessionUser?.userId ?? "").trim();
  const characterIndex = characterIndexForUserId(userId || "guest");
  return {
    character: `character_${characterIndex + 1}`,
    characterIndex,
  };
}

function readSessionUser() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return null;
    const u = JSON.parse(raw);
    const sessionId = u?.sessionId != null ? String(u.sessionId).trim() : "";
    const userId = String(u?.userId ?? u?.userid ?? "").trim();
    if (!sessionId || !userId) return null;
    return { ...u, sessionId, userId };
  } catch {
    return null;
  }
}

const GUEST_LOBBY_USER_KEY = "truthLobbyGuestUserId";
const GUEST_LOBBY_SESSION_KEY = "truthLobbyGuestSessionId";

function getOrCreateGuestLobbyUserId() {
  try {
    let id = sessionStorage.getItem(GUEST_LOBBY_USER_KEY);
    if (id && String(id).trim()) return String(id).trim();
    id = `guest_${crypto.randomUUID()}`;
    sessionStorage.setItem(GUEST_LOBBY_USER_KEY, id);
    return id;
  } catch {
    return `guest_${crypto.randomUUID()}`;
  }
}

/** Stable parent doc id under `lobby/` when there is no signed-in session. */
function getOrCreateGuestLobbySessionId() {
  try {
    let id = sessionStorage.getItem(GUEST_LOBBY_SESSION_KEY);
    if (id && String(id).trim()) return String(id).trim();
    id = crypto.randomUUID();
    sessionStorage.setItem(GUEST_LOBBY_SESSION_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

/**
 * Who to store under `rooms/.../userdata/{userId}`: real session user, or a stable guest id
 * if `userData` is missing (otherwise that subcollection never gets writes).
 */
function readLobbyAttendeeUser(lobbyParentSessionId) {
  const u = readSessionUser();
  if (u?.userId) return u;
  return {
    userId: getOrCreateGuestLobbyUserId(),
    sessionId: lobbyParentSessionId,
    name: "",
    email: "",
    isLobbyGuest: true,
  };
}

const LOBBY_NAME_ALPHANUM =
  "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";

/**
 * Default lobby display name: random letters and digits until the user edits.
 * (Identifier is legacy; output is alphanumeric, not digits-only.)
 */
function randomNumericLobbyName(length = 10) {
  let s = "";
  for (let i = 0; i < length; i += 1) {
    s += LOBBY_NAME_ALPHANUM[
      Math.floor(Math.random() * LOBBY_NAME_ALPHANUM.length)
    ];
  }
  return s;
}

/** Query params on /userpage when opened from a copied invite link. */
function parseShareLinkSearch(searchString) {
  try {
    const params = new URLSearchParams(
      searchString.startsWith("?") ? searchString : `?${searchString}`
    );
    const truthy = (v) =>
      ["1", "true", "yes"].includes(String(v ?? "").toLowerCase());
    const fromShare =
      truthy(params.get("fromShare")) || truthy(params.get("shared"));
    const roomName = String(
      params.get("lobbyName") ??
        params.get("room") ??
        params.get("roomName") ??
        ""
    ).trim();
    const firestoreSessionId = String(
      params.get("session") ?? params.get("sid") ?? ""
    ).trim();
    const lobbyRoomDocId = String(params.get("lobby") ?? "").trim().slice(0, 200);
    return {
      fromShare,
      roomName: roomName.slice(0, 80),
      firestoreSessionId: firestoreSessionId.slice(0, 200),
      lobbyRoomDocId,
    };
  } catch {
    return {
      fromShare: false,
      roomName: "",
      firestoreSessionId: "",
      lobbyRoomDocId: "",
    };
  }
}

function buildUserpageShareUrl(
  origin,
  lobbyId,
  roomDisplayName,
  firestoreSessionId
) {
  const params = new URLSearchParams();
  params.set("lobby", String(lobbyId ?? ""));
  params.set("fromShare", "1");
  params.set("shared", "1");
  const sid = String(firestoreSessionId ?? "").trim();
  if (sid) params.set("session", sid);
  const room = String(roomDisplayName ?? "").trim();
  if (room) {
    params.set("lobbyName", room);
    params.set("room", room);
  }
  return `${origin.replace(/\/$/, "")}/userpage?${params.toString()}`;
}

function clientSnapshotFromLobbyDoc(lobbyData) {
  const durationPresetIndex =
    typeof lobbyData.durationPresetIndex === "number"
      ? lobbyData.durationPresetIndex
      : 0;
  const presetFromDoc =
    DURATION_PRESETS[durationPresetIndex] ?? DURATION_PRESETS[0];
  const createdAt = lobbyData.createdAt;
  let createdAtMs = Date.now();
  if (createdAt && typeof createdAt.toMillis === "function") {
    createdAtMs = createdAt.toMillis();
  } else if (typeof createdAt === "number") {
    createdAtMs = createdAt;
  }
  return {
    sessionId: lobbyData.sessionId,
    lobbyId: lobbyData.lobbyId,
    name: lobbyData.name,
    presenterName: lobbyData.presenterName,
    presenterUserId: lobbyData.presenterUserId,
    playerName: lobbyData.playerName,
    playerUserId: lobbyData.playerUserId,
    gameTitle: lobbyData.gameTitle ?? "Truth & Trick",
    maxPlayers: lobbyData.maxPlayers ?? 5,
    limitEntries: lobbyData.limitEntries !== false,
    durationPresetIndex,
    durationLabel: lobbyData.durationLabel ?? presetFromDoc.label,
    durationSub: lobbyData.durationSub ?? presetFromDoc.sub,
    durationEmoji: lobbyData.durationEmoji ?? presetFromDoc.emoji,
    baseMinutes: lobbyData.baseMinutes ?? presetFromDoc.baseMinutes,
    perPlayerSeconds:
      lobbyData.perPlayerSeconds ?? presetFromDoc.perPlayerSeconds,
    gameMode: lobbyData.gameMode ?? "presenter",
    status: lobbyData.status ?? "waiting",
    createdAt: createdAtMs,
    userCount: lobbyData.userCount ?? 0,
  };
}

function presenterSessionFields(u, gameMode) {
  if (gameMode !== "presenter" || !u?.userId) return {};
  const presenterName =
    String(u.name ?? "").trim() ||
    String(u.email ?? "").trim() ||
    "";
  return { presenterName, presenterUserId: u.userId };
}

function playerSessionFields(u, gameMode) {
  if (gameMode !== "player" || !u?.userId) return {};
  const playerName =
    String(u.name ?? "").trim() ||
    String(u.email ?? "").trim() ||
    "";
  return { playerName, playerUserId: u.userId };
}

/** Denormalized role fields merged onto the room document (see also `userdata` subcollection). */
function roomCreatorFields(sessionUser, gameMode) {
  if (!sessionUser?.userId) return { gameMode };
  return {
    gameMode,
    ...presenterSessionFields(sessionUser, gameMode),
    ...playerSessionFields(sessionUser, gameMode),
  };
}

/** Firestore-safe id derived from display lobby name (may be empty). */
function lobbyRoomDocIdFromDisplayName(name) {
  const s = String(name)
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9_-]/g, "");
  return s.length > 0 ? s.slice(0, 120) : "";
}

function isProbablyLobbyDocument(data) {
  if (!data || typeof data !== "object") return false;
  if (data.gameTitle != null || typeof data.durationPresetIndex === "number") {
    return true;
  }
  if (data.status === "waiting" && data.name != null) return true;
  if (data.maxPlayers != null && data.lobbyId != null) return true;
  return false;
}

function pickRoomDocFromSnapshot(snapshot, resolvedLobbyName) {
  const docs = snapshot.docs;
  if (!docs.length) return null;
  const wantId = lobbyRoomDocIdFromDisplayName(resolvedLobbyName);
  if (wantId) {
    const byId = docs.find((d) => d.id === wantId);
    if (byId) return byId;
  }
  const nm = resolvedLobbyName.trim().toLowerCase();
  if (nm) {
    const byName = docs.find(
      (d) => String(d.data()?.name ?? "").trim().toLowerCase() === nm
    );
    if (byName) return byName;
  }
  return docs[0];
}

/** Prefer explicit `lobby` URL room doc id, then display-name heuristics. */
function pickRoomDocWithHints(snapshot, { lobbyRoomDocId, nameHint }) {
  const docs = snapshot.docs;
  if (!docs.length) return null;
  const idHint = String(lobbyRoomDocId ?? "").trim();
  if (idHint) {
    const byParam = docs.find((d) => d.id === idHint);
    if (byParam) return byParam;
  }
  const hint = String(nameHint ?? "").trim() || "Truth & Trick";
  return pickRoomDocFromSnapshot(snapshot, hint);
}

function lobbyRoomUserdataDocRef(sessionPathSid, roomDocId, userId) {
  return doc(
    database,
    LOBBY_COLLECTION,
    sessionPathSid,
    "rooms",
    roomDocId,
    LOBBY_ROOM_USERDATA_SUBCOLLECTION,
    userId
  );
}

/** Attendee doc for `lobby/{sessionId}/rooms/{roomId}/userdata/{userId}` (role + status; `roomId` only if presenter). */
function buildLobbyUserdataDocPayload(
  sessionUser,
  gameMode,
  roomDocId,
  lobbySessionId
) {
  const userId =
    sessionUser?.userId != null ? String(sessionUser.userId).trim() : "";
  const sid = String(lobbySessionId ?? "").trim();
  const rid = String(roomDocId ?? "").trim();
  const payload = {
    userId,
    gameMode,
    status: "waiting",
    live:true,
    lobbySessionId: sid,
    ...(gameMode === "presenter" && rid ? { roomId: rid } : {}),
    joinedAt: serverTimestamp(),
    organizationId: String(sessionUser?.organizationId ?? "").trim() || null,
    name: String(sessionUser?.name ?? "").trim() || null,
    email: String(sessionUser?.email ?? "").trim() || null,
    isLobbyGuest: Boolean(sessionUser?.isLobbyGuest),
    ...characterFieldsForUser(sessionUser),
    ...presenterSessionFields(sessionUser, gameMode),
    ...playerSessionFields(sessionUser, gameMode),
  };
  if (!payload.organizationId) delete payload.organizationId;
  if (!payload.name) delete payload.name;
  if (!payload.email) delete payload.email;
  if (!payload.isLobbyGuest) delete payload.isLobbyGuest;
  return payload;
}

async function persistLobbyUserdataDoc(
  sessionPathSid,
  roomDocId,
  sessionUser,
  gameMode
) {
  if (!sessionUser?.userId || !roomDocId || !sessionPathSid) return;
  await setDoc(
    lobbyRoomUserdataDocRef(sessionPathSid, roomDocId, sessionUser.userId),
    buildLobbyUserdataDocPayload(
      sessionUser,
      gameMode,
      roomDocId,
      sessionPathSid
    )
  );
}

function UserPage({ updateHeader }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

    useEffect(() => {
      updateHeader({
        backButtonRedirect: "/rules",
        // ...(companyLogo ? { imageUrl: companyLogo } : {}),
      });
    }, []);

  // Check for demo bypass and redirect to plans page
  useEffect(() => {
    const u = readSessionUser();
    if (u?.sessionId?.startsWith("demobypass")) {
      window.setTimeout(() => {
        Swal.fire({
          title: "🎮 Upgrade Your Experience!",
          html: `
            <div style="text-align: center; padding: 20px;">
              <p style="font-size: 16px; color: #666; margin-bottom: 15px;">
                You're currently in demo mode
              </p>
              <p style="font-size: 18px; font-weight: 600; color: #333; margin-bottom: 20px;">
                Subscribe to any PLAN to play with your peers
              </p>
              
            </div>
          `,
          icon: undefined,
          showConfirmButton: true,
          confirmButtonText: "OK",
          confirmButtonColor: "#667eea",
          backdrop: `
            rgba(0,0,0,0.4)
            left top
            no-repeat
          `,
          customClass: {
            popup: 'fancy-popup',
            title: 'fancy-title',
            content: 'fancy-content'
          }
        }).then((result) => {
          if (result.isConfirmed) {
            redirectToPlansPage(navigate);
          }
        });
      }, 0);
    }
  }, [navigate]);
  const [durationIndex, setDurationIndex] = useState(0);
  const [maxPlayers, setMaxPlayers] = useState(5);
  const [limitEntries, setLimitEntries] = useState(true);
  const [lobbyName, setLobbyName] = useState(() => {
    const { fromShare, roomName } = parseShareLinkSearch(
      typeof window !== "undefined" ? window.location.search : ""
    );
    if (fromShare && roomName) return roomName;
    return randomNumericLobbyName();
  });
  const [gameMode, setGameMode] = useState(() => "presenter");
  const [gameModeConfirmed, setGameModeConfirmed] = useState(false);
  /** Another user holds presenter (userdata and/or room doc). */
  const [presenterLockFromUserdata, setPresenterLockFromUserdata] =
    useState(false);
  const [presenterLockFromRoom, setPresenterLockFromRoom] = useState(false);
  const presenterTakenByOther =
    presenterLockFromUserdata || presenterLockFromRoom;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /** Reuse one Firestore lobby for Copy then Play in the same visit */
  const lobbyCacheRef = useRef(null);
  /** `lobby/{sessionId}/rooms/{roomDocId}` when a room doc is known (bootstrap or cache). */
  const activeLobbyRoomRef = useRef({ sessionId: "", roomDocId: "" });

  const preset = DURATION_PRESETS[durationIndex];
  const gameModeInfo = GAME_MODE_INFO[gameMode] ?? GAME_MODE_INFO.presenter;

  const shareLinkLobby = useMemo(
    () => parseShareLinkSearch(searchParams.toString()),
    [searchParams]
  );
  /** Display name for Firestore + host: URL wins when opened from a share link (avoids state/effect race). */
  const resolvedName = useMemo(() => {
    const urlRoom = String(shareLinkLobby.roomName ?? "").trim();
    if (shareLinkLobby.fromShare && urlRoom) return urlRoom;
    return lobbyName.trim() || "Truth & Trick";
  }, [shareLinkLobby.fromShare, shareLinkLobby.roomName, lobbyName]);
  const isLobbyNameLockedFromShare =
    shareLinkLobby.fromShare && shareLinkLobby.roomName.length > 0;
  
  // Track if current room is finished to show appropriate message
  const [currentRoomFinished, setCurrentRoomFinished] = useState(false);
  
  // Track current number of users in the lobby
  const [currentUserCount, setCurrentUserCount] = useState(0);
  const [firestoreUserCount, setFirestoreUserCount] = useState(0);
  const [lobbyDurationLabel, setLobbyDurationLabel] = useState("");
  const [lobbyPerPlayerSeconds, setLobbyPerPlayerSeconds] = useState(null);
  
  // Track max player limit popup
  const [showMaxPlayerPopup, setShowMaxPlayerPopup] = useState(false);
  const [initialPageLoading, setInitialPageLoading] = useState(true);
  const [playerModeClickLocked, setPlayerModeClickLocked] = useState(false);
  const playerModeClickLockTimeoutRef = useRef(null);
  
  /** Presenter-only lobby fields; players join with host’s settings. */
  const lobbySettingsReadOnlyForPlayer = gameMode === "player";
  const effectiveDurationLabel =
    gameMode === "player" && lobbyDurationLabel ? lobbyDurationLabel : preset.label;
  const effectivePerPlayerSeconds =
    gameMode === "player" && Number.isFinite(lobbyPerPlayerSeconds)
      ? lobbyPerPlayerSeconds
      : preset.perPlayerSeconds;
  const effectiveDurationSub =
    gameMode === "player"
      ? `Per player: ${effectivePerPlayerSeconds} seconds`
      : preset.sub;
  const effectiveMaxPlayers = maxPlayers;
  const maxPlayersStepValue = effectiveMaxPlayers;

  const playerModeToggleSelected = gameMode === "player";
  const playerModeGlobalInteractionLocked = playerModeClickLocked;
  const shouldShowLoadingCover =
    initialPageLoading || playerModeGlobalInteractionLocked;
  const loadingCoverMessage = initialPageLoading
    ? "Preparing lobby..."
    : "Switching to Player mode...";
  const requiresExplicitPlayerSelection = shareLinkLobby.fromShare;
  const playerPlayLockedUntilPlayerMode =
    requiresExplicitPlayerSelection && !playerModeToggleSelected;
  const playerModeAutoSelectionPending =
    presenterTakenByOther && !playerModeToggleSelected;

  const playerPlayLockedForModeSelection =
    playerModeAutoSelectionPending ||
    (playerModeToggleSelected && !gameModeConfirmed);

  const isPlayDisabled =
    busy ||
    shouldShowLoadingCover ||
    playerPlayLockedForModeSelection ||
    playerPlayLockedUntilPlayerMode ||
    playerModeGlobalInteractionLocked;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setInitialPageLoading(false);
    }, LANDING_LOADER_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (playerModeClickLockTimeoutRef.current) {
        window.clearTimeout(playerModeClickLockTimeoutRef.current);
        playerModeClickLockTimeoutRef.current = null;
      }
    };
  }, []);

  const startPlayerModeClickLock = useCallback(() => {
    setPlayerModeClickLocked(true);
    if (playerModeClickLockTimeoutRef.current) {
      window.clearTimeout(playerModeClickLockTimeoutRef.current);
    }
  }, []);

  const finishPlayerModeClickLock = useCallback((lockStartTs) => {
    const elapsed = Date.now() - lockStartTs;
    const remaining = Math.max(0, PLAYER_MODE_CLICK_LOCK_MS - elapsed);
    if (playerModeClickLockTimeoutRef.current) {
      window.clearTimeout(playerModeClickLockTimeoutRef.current);
    }
    playerModeClickLockTimeoutRef.current = window.setTimeout(() => {
      setPlayerModeClickLocked(false);
      playerModeClickLockTimeoutRef.current = null;
    }, remaining);
  }, []);

  /** Sync lobby name from share URL when `room` / `lobbyName` query changes (before Firestore bootstrap may override). */
  useEffect(() => {
    if (!shareLinkLobby.fromShare) return;
    const rn = String(shareLinkLobby.roomName ?? "").trim();
    if (!rn) return;
    setLobbyName(rn);
  }, [shareLinkLobby.fromShare, shareLinkLobby.roomName]);


  useEffect(() => {
    const u = readSessionUser();
    if (!u) return undefined;
    const sl = parseShareLinkSearch(searchParams.toString());
    if (sl.fromShare && String(sl.firestoreSessionId ?? "").trim()) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(
          doc(database, `sessions/${u.sessionId}/users/${u.userId}`)
        );
        if (cancelled || !snap.exists()) return;
        const m = snap.data()?.gameMode;
        if (m === "presenter" || m === "player") setGameMode(m);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  /**
   * Resolve `lobby/{sid}/rooms`: sync lobby display name; subscribe to room doc + userdata for presenter lock.
   * `sid` = URL `session` or signed-in `userData.sessionId`.
   */
  useEffect(() => {
    let cancelled = false;
    let detachUserdata = () => {};
    let detachRoom = () => {};

    const sid =
      String(shareLinkLobby.firestoreSessionId ?? "").trim() ||
      String(readSessionUser()?.sessionId ?? "").trim();

    if (!sid) {
      activeLobbyRoomRef.current = { sessionId: "", roomDocId: "" };
      setPresenterLockFromUserdata(false);
      setPresenterLockFromRoom(false);
      setCurrentUserCount(0);
      setFirestoreUserCount(0);
      setLobbyDurationLabel("");
      setLobbyPerPlayerSeconds(null);
      return undefined;
    }

    const nameHint =
      String(shareLinkLobby.roomName ?? "").trim() || "Truth & Trick";
    const lobbyRoomDocIdHint = String(shareLinkLobby.lobbyRoomDocId ?? "").trim();
    let cachedRoomIdHint = "";
    try {
      const raw = sessionStorage.getItem("truthLobby");
      if (raw) {
        const parsed = JSON.parse(raw);
        cachedRoomIdHint = String(parsed?.lobbyId ?? "").trim();
      }
    } catch {
      cachedRoomIdHint = "";
    }
    const roomDocIdHint = lobbyRoomDocIdHint || cachedRoomIdHint;

    (async () => {
      try {
        let roomDoc = null;
        if (roomDocIdHint) {
          const directRoomRef = doc(
            database,
            LOBBY_COLLECTION,
            sid,
            "rooms",
            roomDocIdHint
          );
          const directRoomSnap = await getDoc(directRoomRef);
          if (directRoomSnap.exists()) {
            roomDoc = directRoomSnap;
          }
        }

        if (!roomDoc) {
          const roomsSnap = await getDocs(
            collection(database, LOBBY_COLLECTION, sid, "rooms")
          );
          if (cancelled) return;

          if (roomsSnap.empty) {
            activeLobbyRoomRef.current = { sessionId: "", roomDocId: "" };
            if (!isLobbyNameLockedFromShare) {
              setLobbyName((prev) => {
                const t = String(prev ?? "").trim();
                return t.length > 0 ? t : randomNumericLobbyName();
              });
            }
            setPresenterLockFromUserdata(false);
            setPresenterLockFromRoom(false);
            setCurrentUserCount(0);
            setFirestoreUserCount(0);
            setLobbyDurationLabel("");
            setLobbyPerPlayerSeconds(null);
            return;
          }

          roomDoc = pickRoomDocWithHints(roomsSnap, {
            lobbyRoomDocId: roomDocIdHint,
            nameHint,
          });
        }

        if (cancelled) return;
        if (!roomDoc) {
          activeLobbyRoomRef.current = { sessionId: "", roomDocId: "" };
          setPresenterLockFromUserdata(false);
          setPresenterLockFromRoom(false);
          setCurrentUserCount(0);
          setFirestoreUserCount(0);
          setLobbyDurationLabel("");
          setLobbyPerPlayerSeconds(null);
          return;
        }

        const display = String(roomDoc.data()?.name ?? "").trim();
        const roomData = roomDoc.data();
        
        // Check if current room is finished
        if (roomData?.gameFinished === true) {
          console.log("Current room is finished, showing room closed message");
          setCurrentRoomFinished(true);
        } else {
          setCurrentRoomFinished(false);
        }
        
        setLobbyName(display || randomNumericLobbyName());

        const me = readLobbyAttendeeUser(sid);
        const selfUserId = String(me?.userId ?? "").trim();
        if (!selfUserId) {
          activeLobbyRoomRef.current = { sessionId: "", roomDocId: "" };
          setPresenterLockFromUserdata(false);
          setPresenterLockFromRoom(false);
          setCurrentUserCount(0);
          setFirestoreUserCount(0);
          return;
        }

        activeLobbyRoomRef.current = {
          sessionId: sid,
          roomDocId: roomDoc.id,
        };
        const roomRef = doc(
          database,
          LOBBY_COLLECTION,
          sid,
          "rooms",
          roomDoc.id
        );
        detachRoom = onSnapshot(
          roomRef,
          (snap) => {
            if (!snap.exists()) return;
            const d = snap.data();
            console.log("d", d);
            const mp = d?.maxPlayers;
            // alert(mp);
            if (typeof mp === "number" && Number.isFinite(mp)) {
              const c = Math.min(
                MAX_PLAYERS,
                Math.max(MIN_PLAYERS, Math.floor(mp))
              );
              setMaxPlayers(c);
            }
            // Keep backwards compatibility with any older/mismatched count keys.
            const roomUserCountFromDoc = Number(
              d?.userCount ?? d?.usercount ?? d?.usersCount
            );
            if (Number.isFinite(roomUserCountFromDoc)) {
              setFirestoreUserCount(Math.max(0, Math.floor(roomUserCountFromDoc)));
            }
            const idx = Number.isInteger(d?.durationPresetIndex)
              ? d.durationPresetIndex
              : -1;
            const presetFromDoc = DURATION_PRESETS[idx] ?? null;
            const durationLabelFromDoc = String(d?.durationLabel ?? "").trim();
            const perPlayerFromDoc = Number(d?.perPlayerSeconds);
            const safePerPlayerSeconds = Number.isFinite(perPlayerFromDoc)
              ? Math.max(1, Math.floor(perPlayerFromDoc))
              : presetFromDoc?.perPlayerSeconds ?? null;
            setLobbyDurationLabel(durationLabelFromDoc || presetFromDoc?.label || "");
            setLobbyPerPlayerSeconds(safePerPlayerSeconds);
            const taken =
              d?.gameMode === "presenter" &&
              d?.presenterUserId &&
              String(d.presenterUserId).trim() !== selfUserId;
            setPresenterLockFromRoom(Boolean(taken));
          },
          () => setPresenterLockFromRoom(false)
        );

        const userdataCol = collection(
          database,
          LOBBY_COLLECTION,
          sid,
          "rooms",
          roomDoc.id,
          LOBBY_ROOM_USERDATA_SUBCOLLECTION
        );
        detachUserdata = onSnapshot(
          userdataCol,
          (snap) => {
            const taken = snap.docs.some(
              (d) =>
                d.id !== selfUserId && d.data()?.gameMode === "presenter"
            );
            setPresenterLockFromUserdata(taken);
            // Count current users
            setCurrentUserCount(snap.docs.length);
            // Fallback source for user count when room doc has no userCount yet.
            setFirestoreUserCount(snap.docs.length);
          },
          () => {
            setPresenterLockFromUserdata(false);
            setCurrentUserCount(0);
          }
        );
      } catch {
        if (!cancelled) {
          activeLobbyRoomRef.current = { sessionId: "", roomDocId: "" };
          setPresenterLockFromUserdata(false);
          setPresenterLockFromRoom(false);
          setCurrentUserCount(0);
          setFirestoreUserCount(0);
          setLobbyDurationLabel("");
          setLobbyPerPlayerSeconds(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      detachRoom();
      detachUserdata();
      activeLobbyRoomRef.current = { sessionId: "", roomDocId: "" };
      setPresenterLockFromUserdata(false);
      setPresenterLockFromRoom(false);
      setCurrentUserCount(0);
      setFirestoreUserCount(0);
      setLobbyDurationLabel("");
      setLobbyPerPlayerSeconds(null);
    };
  }, [
    shareLinkLobby.firestoreSessionId,
    shareLinkLobby.roomName,
    shareLinkLobby.fromShare,
    shareLinkLobby.lobbyRoomDocId,
    isLobbyNameLockedFromShare,
  ]);

  const persistGameModeToUserDoc = useCallback(async (mode) => {
    const u = readSessionUser();
    if (!u) return false;
    try {
      const userDocRef = doc(
        database,
        `sessions/${u.sessionId}/users/${u.userId}`
      );
      await setDoc(
        userDocRef,
        {
          userId: u.userId,
          sessionId: u.sessionId,
          gameMode: mode,
          gameModeUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setError("");
      return true;
    } catch (e) {
      console.error("gameMode sync:", e);
      setError(
        e?.message ||
          "Could not save game mode. Check Firestore rules for your user document."
      );
      return false;
    }
  }, []);

  const selectGameMode = useCallback(
    async (mode, opts = {}) => {
      if (mode === "presenter" && presenterTakenByOther) return;
      const confirmedByUser = opts?.confirmedByUser === true;
      const lockPlayerModeSelection = mode === "player" && confirmedByUser;
      const lockStartTs = lockPlayerModeSelection ? Date.now() : 0;
      if (lockPlayerModeSelection) {
        startPlayerModeClickLock();
      } else if (mode !== "player") {
        setPlayerModeClickLocked(false);
        if (playerModeClickLockTimeoutRef.current) {
          window.clearTimeout(playerModeClickLockTimeoutRef.current);
          playerModeClickLockTimeoutRef.current = null;
        }
      }
      
      // Prevent first user (creator) from switching to player
      // if (currentUserCount === 0 && mode === "player") {
      //   await Swal.fire({
      //     icon: 'warning',
      //     title: 'Cannot Switch to Player',
      //     text: 'As the first user to join this session, you must remain the presenter. Other users can join as players.',
      //     confirmButtonColor: '#667eea',
      //     confirmButtonText: 'OK'
      //   });
      //   return;
      // }
      
      setGameMode(mode);
      setGameModeConfirmed(mode === "player" ? true : confirmedByUser);
      await persistGameModeToUserDoc(mode);
      if (lockPlayerModeSelection) {
        finishPlayerModeClickLock(lockStartTs);
      }
    },
    [
      finishPlayerModeClickLock,
      persistGameModeToUserDoc,
      presenterTakenByOther,
      currentUserCount,
      startPlayerModeClickLock,
    ]
  );

  useEffect(() => {
    if (!presenterTakenByOther || gameMode !== "presenter") return;
    selectGameMode("player", { confirmedByUser: false });
  }, [presenterTakenByOther, gameMode, selectGameMode]);

  // Ensure first user is always presenter
  useEffect(() => {
    if (currentUserCount === 0 && gameMode !== "presenter" && !presenterTakenByOther) {
      setGameMode("presenter");
      persistGameModeToUserDoc("presenter");
    }
  }, [currentUserCount, gameMode, presenterTakenByOther, persistGameModeToUserDoc]);

  const syncMaxPlayersToFirestore = useCallback(
    async (nextCount) => {
      if (gameMode !== "presenter" || presenterTakenByOther) return;
      let sid = String(activeLobbyRoomRef.current.sessionId ?? "").trim();
      let rid = String(activeLobbyRoomRef.current.roomDocId ?? "").trim();
      if (!sid || !rid) {
        const cached = lobbyCacheRef.current;
        if (cached?.sessionId && cached?.lobbyId) {
          sid = String(cached.sessionId).trim();
          rid = String(cached.lobbyId).trim();
        }
      }
      if (!sid || !rid) return;
      const n = Math.min(
        MAX_PLAYERS,
        Math.max(
          MIN_PLAYERS,
          Math.floor(Number(nextCount)) || MIN_PLAYERS
        )
      );
      try {
        await setDoc(
          doc(database, LOBBY_COLLECTION, sid, "rooms", rid),
          { maxPlayers: n },
          { merge: true }
        );
        if (lobbyCacheRef.current?.payload) {
          lobbyCacheRef.current.payload.maxPlayers = n;
        }
        try {
          const raw = sessionStorage.getItem("truthLobby");
          if (raw) {
            const o = JSON.parse(raw);
            if (o && typeof o === "object") {
              o.maxPlayers = n;
              sessionStorage.setItem("truthLobby", JSON.stringify(o));
            }
          }
        } catch {
          /* ignore */
        }
        setError("");
      } catch (e) {
        console.error("maxPlayers sync:", e);
        setError(
          e?.message ||
            "Could not update max players. Check Firestore rules."
        );
      }
    },
    [gameMode, presenterTakenByOther]
  );

  const decDuration = useCallback(() => {
    if (gameMode === "player") return;
    setDurationIndex((i) => (i <= 0 ? DURATION_PRESETS.length - 1 : i - 1));
  }, [gameMode]);

  const incDuration = useCallback(() => {
    if (gameMode === "player") return;
    setDurationIndex((i) => (i >= DURATION_PRESETS.length - 1 ? 0 : i + 1));
  }, [gameMode]);

  const decPlayers = useCallback(() => {
    if (gameMode === "player") return;
    setMaxPlayers((n) => {
      const next = Math.max(MIN_PLAYERS, n - 1);
      if (next !== n) void syncMaxPlayersToFirestore(next);
      return next;
    });
  }, [gameMode, syncMaxPlayersToFirestore]);

  const incPlayers = useCallback(() => {
    if (gameMode === "player") return;
    setMaxPlayers((n) => {
      const next = Math.min(MAX_PLAYERS, n + 1);
      if (next !== n) void syncMaxPlayersToFirestore(next);
      return next;
    });
  }, [gameMode, syncMaxPlayersToFirestore]);

  const buildLobbyPayload = useCallback(
    (lobbyId) => ({
      lobbyId,
      name: resolvedName,
      gameTitle: "Truths & Tricks",
      maxPlayers,
      limitEntries,
      durationPresetIndex: durationIndex,
      durationLabel: preset.label,
      durationSub: preset.sub,
      durationEmoji: preset.emoji,
      baseMinutes: preset.baseMinutes,
      perPlayerSeconds: preset.perPlayerSeconds,
      gameMode,
      createdAt: serverTimestamp(),
      status: "waiting",
    }),
    [
      resolvedName,
      maxPlayers,
      limitEntries,
      durationIndex,
      gameMode,
      preset.label,
      preset.sub,
      preset.emoji,
      preset.baseMinutes,
      preset.perPlayerSeconds,
    ]
  );

  const persistLobby = useCallback(async () => {
    setError("");
    if (lobbyCacheRef.current) {
      if (readSessionUser()) {
        await persistGameModeToUserDoc(gameMode);
      }
      const urlName = String(shareLinkLobby.roomName ?? "").trim();
      const payload = {
        ...lobbyCacheRef.current.payload,
        fromShareableLink: Boolean(shareLinkLobby.fromShare),
        ...(shareLinkLobby.fromShare && urlName ? { name: urlName } : {}),
      };
      sessionStorage.setItem("truthLobby", JSON.stringify(payload));
      const sid = String(lobbyCacheRef.current.sessionId ?? "").trim();
      const rid = String(lobbyCacheRef.current.lobbyId ?? "").trim();
      if (sid && rid) {
        activeLobbyRoomRef.current = { sessionId: sid, roomDocId: rid };
      }
      return { ...lobbyCacheRef.current, payload };
    }
    setBusy(true);
    try {
      const sessionUser = readSessionUser();
      if (sessionUser) {
        await persistGameModeToUserDoc(gameMode);
      }

      const sessionFromShare = String(
        shareLinkLobby.firestoreSessionId ?? ""
      ).trim();
      const joinSharedLobby =
        shareLinkLobby.fromShare && sessionFromShare.length > 0;
      const shareUrlLobbyName =
        shareLinkLobby.fromShare &&
        String(shareLinkLobby.roomName ?? "").trim()
          ? String(shareLinkLobby.roomName).trim()
          : "";
      const sessionIdForGame = joinSharedLobby
        ? sessionFromShare
        : sessionUser?.sessionId && String(sessionUser.sessionId).trim()
          ? String(sessionUser.sessionId).trim()
          : getOrCreateGuestLobbySessionId();

      const attendeeUser = readLobbyAttendeeUser(sessionIdForGame);
      const presenterExtra = presenterSessionFields(attendeeUser, gameMode);
      const playerExtra = playerSessionFields(attendeeUser, gameMode);

      {
        const sid = String(sessionIdForGame).trim();
        const roomsSnap = await getDocs(
          collection(database, LOBBY_COLLECTION, sid, "rooms")
        );

        if (!roomsSnap.empty) {
          const roomDoc = pickRoomDocWithHints(roomsSnap, {
            lobbyRoomDocId: String(shareLinkLobby.lobbyRoomDocId ?? "").trim(),
            nameHint: resolvedName,
          });
          const roomData = roomDoc.data();
          const clientSnapshot = clientSnapshotFromLobbyDoc(roomData);
          clientSnapshot.sessionId = clientSnapshot.sessionId ?? sid;
          clientSnapshot.lobbyId =
            clientSnapshot.lobbyId ?? roomData?.lobbyId ?? roomDoc.id;

          const roleUpdate = roomCreatorFields(attendeeUser, gameMode);
          await setDoc(
            doc(database, LOBBY_COLLECTION, sid, "rooms", roomDoc.id),
            roleUpdate,
            { merge: true }
          );
          Object.assign(clientSnapshot, roleUpdate);
          if (shareUrlLobbyName) {
            clientSnapshot.name = shareUrlLobbyName;
          }
          clientSnapshot.fromShareableLink = Boolean(shareLinkLobby.fromShare);
          await persistLobbyUserdataDoc(
            sid,
            roomDoc.id,
            attendeeUser,
            gameMode
          );

          lobbyCacheRef.current = {
            sessionId: clientSnapshot.sessionId ?? sid,
            lobbyId: clientSnapshot.lobbyId ?? roomDoc.id,
            payload: clientSnapshot,
          };
          activeLobbyRoomRef.current = {
            sessionId: lobbyCacheRef.current.sessionId,
            roomDocId: lobbyCacheRef.current.lobbyId,
          };
          sessionStorage.setItem(
            "truthLobby",
            JSON.stringify(clientSnapshot)
          );
          return lobbyCacheRef.current;
        }

        let lobbySnap = await getDoc(doc(database, LOBBY_COLLECTION, sid));
        if (!lobbySnap.exists()) {
          const sessionSnap = await getDoc(doc(database, "sessions", sid));
          const legacyLobbyId = sessionSnap.exists()
            ? String(sessionSnap.data()?.lobbyId ?? "").trim()
            : "";
          if (legacyLobbyId) {
            lobbySnap = await getDoc(
              doc(database, LOBBY_COLLECTION, legacyLobbyId)
            );
          }
        }

        if (lobbySnap.exists() && isProbablyLobbyDocument(lobbySnap.data())) {
          const flatData = lobbySnap.data();
          const clientSnapshot = clientSnapshotFromLobbyDoc(flatData);
          clientSnapshot.sessionId = clientSnapshot.sessionId ?? sid;
          const roomDocIdForUsers =
            String(flatData?.lobbyId ?? "").trim() || lobbySnap.id;
          clientSnapshot.lobbyId =
            clientSnapshot.lobbyId ?? flatData?.lobbyId ?? lobbySnap.id;

          const roleUpdate = roomCreatorFields(attendeeUser, gameMode);
          await setDoc(
            doc(
              database,
              LOBBY_COLLECTION,
              sid,
              "rooms",
              roomDocIdForUsers
            ),
            roleUpdate,
            { merge: true }
          );
          Object.assign(clientSnapshot, roleUpdate);
          if (shareUrlLobbyName) {
            clientSnapshot.name = shareUrlLobbyName;
          }
          clientSnapshot.fromShareableLink = Boolean(shareLinkLobby.fromShare);
          await persistLobbyUserdataDoc(
            sid,
            roomDocIdForUsers,
            attendeeUser,
            gameMode
          );

          lobbyCacheRef.current = {
            sessionId: clientSnapshot.sessionId ?? sid,
            lobbyId: clientSnapshot.lobbyId ?? roomDocIdForUsers,
            payload: clientSnapshot,
          };
          activeLobbyRoomRef.current = {
            sessionId: lobbyCacheRef.current.sessionId,
            roomDocId: lobbyCacheRef.current.lobbyId,
          };
          sessionStorage.setItem(
            "truthLobby",
            JSON.stringify(clientSnapshot)
          );
          return lobbyCacheRef.current;
        }
      }

      const roomDocId =
        lobbyRoomDocIdFromDisplayName(resolvedName) || sessionIdForGame;
      const payload = buildLobbyPayload(roomDocId);
      await setDoc(
        doc(
          database,
          LOBBY_COLLECTION,
          sessionIdForGame,
          "rooms",
          roomDocId
        ),
        { ...payload, ...presenterExtra, ...playerExtra }
      );
      await persistLobbyUserdataDoc(
        sessionIdForGame,
        roomDocId,
        attendeeUser,
        gameMode
      );

      const clientSnapshot = {
        sessionId: sessionIdForGame,
        lobbyId: payload.lobbyId,
        name: shareUrlLobbyName || payload.name,
        ...presenterExtra,
        ...playerExtra,
        gameTitle: payload.gameTitle,
        maxPlayers: payload.maxPlayers,
        limitEntries: payload.limitEntries,
        durationPresetIndex: payload.durationPresetIndex,
        durationLabel: payload.durationLabel,
        durationSub: payload.durationSub,
        durationEmoji: payload.durationEmoji,
        baseMinutes: payload.baseMinutes,
        perPlayerSeconds: payload.perPlayerSeconds,
        gameMode: payload.gameMode,
        status: payload.status,
        createdAt: Date.now(),
        fromShareableLink: Boolean(shareLinkLobby.fromShare),
      };
      lobbyCacheRef.current = {
        sessionId: sessionIdForGame,
        lobbyId: roomDocId,
        payload: clientSnapshot,
      };
      activeLobbyRoomRef.current = {
        sessionId: lobbyCacheRef.current.sessionId,
        roomDocId: lobbyCacheRef.current.lobbyId,
      };
      sessionStorage.setItem("truthLobby", JSON.stringify(clientSnapshot));

      return lobbyCacheRef.current;
    } catch (e) {
      console.error("Lobby create failed:", e);
      setError(
        e?.message ||
          "Could not create lobby. Check Firebase config and Firestore rules."
      );
      return null;
    } finally {
      setBusy(false);
    }
  }, [
    buildLobbyPayload,
    gameMode,
    persistGameModeToUserDoc,
    resolvedName,
    shareLinkLobby.fromShare,
    shareLinkLobby.firestoreSessionId,
    shareLinkLobby.roomName,
    shareLinkLobby.lobbyRoomDocId,
  ]);

  const handlePlay = async () => {
    if (playerModeGlobalInteractionLocked) return;
    if (playerPlayLockedUntilPlayerMode) return;
    if (playerModeToggleSelected && !gameModeConfirmed) return;
    // Check if max user limit has been reached
    if (limitEntries && currentUserCount >= maxPlayers) {
      setShowMaxPlayerPopup(true);
      return;
    }
    
    const result = await persistLobby();
    if (!result) return;
    const mode = result.payload?.gameMode ?? gameMode;
    if (mode === "player") {
      navigate("/waiting");
    } else {
      navigate("/host");
    }
  };

  function redirectToPlansPage(navigate) {
  const planUrl =
    typeof process.env.REACT_APP_PLAN_URL === "string"
      ? process.env.REACT_APP_PLAN_URL.trim()
      : "";
  const base =
    typeof process.env.REACT_APP_BASE_URL === "string"
      ? process.env.REACT_APP_BASE_URL.trim().replace(/\/$/, "")
      : "";
  const resolved = planUrl || (base ? `${base}/plans` : "");
  // react-router `navigate("https://…")` is treated as a relative path — use full navigation for absolute URLs
  if (resolved && /^https?:\/\//i.test(resolved)) {
    window.location.assign(resolved);
    return;
  }
  navigate(resolved || "/plans");
}

  const handleCopyLink = async () => {
    setError("");
    const result = await persistLobby();
    if (!result) return;
    const roomLabel =
      String(result.payload?.name ?? "").trim() || resolvedName;
    const url = buildUserpageShareUrl(
      window.location.origin,
      result.lobbyId,
      roomLabel,
      result.sessionId
    );
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      setError("Could not copy link. Copy manually: " + url);
      return;
    }
  };

  const handleClose = () => {
    navigate("/intro");
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="userpage">
      <MonkeyBackdrop />
      {currentRoomFinished ? (
        <div
          className={`userpage__card${
            shouldShowLoadingCover
              ? " userpage__card--interaction-locked"
              : ""
          }`}
        >
          {shouldShowLoadingCover ? (
            <div className="userpage__interaction-lock" role="status" aria-live="polite">
              <span className="userpage__interaction-lock-text">
                <span className="userpage__interaction-lock-spinner" aria-hidden />
                {loadingCoverMessage}
              </span>
            </div>
          ) : null}
          
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2 style={{ 
              color: 'white', 
              marginBottom: '20px',
              fontSize: '28px',
              fontWeight: '700',
              fontFamily: 'Montserrat, sans-serif'
            }}>Game's over 🎉 😊</h2>
            <p style={{ 
              fontSize: '18px', 
              color: 'white',
              fontWeight: '500',
              fontFamily: 'Montserrat, sans-serif',
              lineHeight: '1.5'
            }}>
              This room is now closed.</p>
          </div>
        </div>
      ) : (
        <div
          className={`userpage__card${
            shouldShowLoadingCover
              ? " userpage__card--interaction-locked"
              : ""
          }`}
        >
          {shouldShowLoadingCover ? (
            <div className="userpage__interaction-lock" role="status" aria-live="polite">
              <span className="userpage__interaction-lock-text">
                <span className="userpage__interaction-lock-spinner" aria-hidden />
                {loadingCoverMessage}
              </span>
            </div>
          ) : null}
          <button
            type="button"
            className="userpage__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>

          <h1 className="userpage__title">Truth &amp; Trick</h1>

        <div className="userpage__name-block">
          <label className="userpage__name-label">
            <span className="userpage__section-title">Lobby name</span>
            <input
              type="text"
              className={`userpage__name-input${
                isLobbyNameLockedFromShare || lobbySettingsReadOnlyForPlayer
                  ? " userpage__name-input--locked"
                  : ""
              }`}
              value={lobbyName}
              onChange={(e) => setLobbyName(e.target.value)}
              placeholder="Rename your lobby"
              maxLength={80}
              disabled={
                isLobbyNameLockedFromShare || lobbySettingsReadOnlyForPlayer
              }
              aria-readonly={
                isLobbyNameLockedFromShare || lobbySettingsReadOnlyForPlayer
              }
              title={
                isLobbyNameLockedFromShare
                  ? "Lobby name is fixed for this invite link"
                  : lobbySettingsReadOnlyForPlayer
                    ? "Lobby settings are controlled by the presenter"
                    : undefined
              }
            />
          </label>
          <p className="userpage__section-sub userpage__name-hint">
            {isLobbyNameLockedFromShare
              ? "This lobby name came from the link you opened and cannot be changed."
              : lobbySettingsReadOnlyForPlayer
                ? "As Player you cannot change the lobby name; the presenter sets it."
                : "You can also set your default lobby name."}
          </p>
          {shareLinkLobby.fromShare &&
          !String(shareLinkLobby.firestoreSessionId ?? "").trim() ? (
            <p className="userpage__section-sub userpage__name-hint" role="status">
              This invite link is missing a session id. Copy a fresh link from the
              host to join as Player and sync roles.
            </p>
          ) : null}
        </div>

        <section
          className="userpage__section userpage__game-mode"
          aria-labelledby="userpage-game-mode-heading"
        >
          <div className="userpage__section-head" id="userpage-game-mode-heading">
            <span className="userpage__section-emoji" aria-hidden>
              {gameModeInfo.emoji}
            </span>
            <div>
              <div className="userpage__section-title">
                Game Mode: {gameModeInfo.label}
              </div>
              <div className="userpage__section-sub">{gameModeInfo.sub}</div>
            </div>
          </div>
          <div
            className="userpage__game-mode-toggle"
            role="group"
            aria-label="Game mode"
          >
            <button
              type="button"
              className={`userpage__game-mode-option${
                gameMode === "presenter" ? " userpage__game-mode-option--active" : ""
              }`}
              onClick={() =>
                selectGameMode("presenter", { confirmedByUser: true })
              }
              aria-pressed={gameMode === "presenter"}
              disabled={presenterTakenByOther}
              title={
                presenterTakenByOther
                  ? "A presenter is already in this lobby session"
                  : undefined
              }
            >
              Presenter
            </button>
            <button
              type="button"
              className={`userpage__game-mode-option${
                gameMode === "player" ? " userpage__game-mode-option--active" : ""
              }${currentUserCount === 0 ? " userpage__game-mode-option--disabled" : ""}`}
              onClick={() => selectGameMode("player", { confirmedByUser: true })}
              aria-pressed={gameMode === "player"}
              disabled={currentUserCount === 0}
              title={
                currentUserCount === 0
                  ? "As the first user to join, you must remain the presenter"
                  : undefined
              }
            >
              Player
            </button>
          </div>
          {currentUserCount === 0 && !presenterTakenByOther ? (
            <p className="userpage__section-sub userpage__game-mode-lock-hint" role="status">
              As the first user to join this session, you are the presenter and cannot switch to player mode.
            </p>
          ) : null}
          {presenterTakenByOther ? (
            <p className="userpage__section-sub userpage__game-mode-lock-hint" role="status">
              A presenter is already in this lobby. You can join as Player.
            </p>
          ) : null}
          {lobbySettingsReadOnlyForPlayer ? (
            <p className="userpage__section-sub userpage__game-mode-lock-hint" role="status">
              Player mode: duration and max players are set by the presenter.
            </p>
          ) : null}
        </section>

        <section className="userpage__section" aria-labelledby="userpage-duration">
          <div className="userpage__section-head" id="userpage-duration">
            <span className="userpage__section-emoji" aria-hidden>
              ⏳
            </span>
            <div>
              <div className="userpage__section-title">
                Duration: {effectiveDurationLabel} (Duration to submit statements)
              </div>
              <div className="userpage__section-sub">{effectiveDurationSub}</div>
            </div>
          </div>
          <div className="userpage__stepper" role="group" aria-label="Duration preset">
            <button
              type="button"
              className="userpage__step userpage__step--minus"
              onClick={decDuration}
              aria-label="Previous duration preset"
              disabled={lobbySettingsReadOnlyForPlayer}
            >
              −
            </button>
            <div className="userpage__step-value" aria-live="polite">
              <span className="userpage__step-emoji" aria-hidden>
                {preset.emoji}
              </span>
            </div>
            <button
              type="button"
              className="userpage__step userpage__step--plus"
              onClick={incDuration}
              aria-label="Next duration preset"
              disabled={lobbySettingsReadOnlyForPlayer}
            >
              +
            </button>
          </div>
        </section>

        <section className="userpage__section" aria-labelledby="userpage-players">
          <div className="userpage__section-head" id="userpage-players">
            <span className="userpage__section-emoji" aria-hidden>
              😎
            </span>
            <div className="userpage__section-title">
              Max Players
              {gameMode === "player" ? `` : ""}
            </div>
          </div>
          <div className="userpage__stepper" role="group" aria-label="Maximum players">
            <button
              type="button"
              className="userpage__step userpage__step--minus"
              onClick={decPlayers}
              disabled={
                lobbySettingsReadOnlyForPlayer || maxPlayers <= MIN_PLAYERS
              }
              aria-label="Decrease max players"
            >
              −
            </button>
            <div className="userpage__step-value userpage__step-value--num">
              {maxPlayersStepValue}
            </div>
            <button
              type="button"
              className="userpage__step userpage__step--plus"
              onClick={incPlayers}
              disabled={
                lobbySettingsReadOnlyForPlayer || maxPlayers >= MAX_PLAYERS
              }
              aria-label="Increase max players"
            >
              +
            </button>
          </div>
        </section>

        <section className="userpage__section userpage__section--row">
          {/* <div className="userpage__limit-label">
            <span className="userpage__section-emoji" aria-hidden>
              📝
            </span>
            <span className="userpage__section-title userpage__section-title--inline">
              Limit Entries
            </span>
            <span
              className="userpage__info"
              title="When enabled, each player can only submit a limited number of entries for this round."
              aria-label="Limit entries info"
            >
              ⓘ
            </span>
          </div> */}
          {/* <Switch
            checked={limitEntries}
            onChange={setLimitEntries}
            onColor="#ffffff"
            offColor="rgba(255,255,255,0.35)"
            onHandleColor="#b0106b"
            offHandleColor="#f8d0e8"
            handleDiameter={22}
            height={28}
            width={52}
            uncheckedIcon={false}
            checkedIcon={false}
            aria-label="Limit entries"
          /> */}
        </section>

        {error ? (
          <p className="userpage__error" role="alert">
            {error}
          </p>
        ) : null}

        {currentRoomFinished ? (
          <p className="userpage__error" role="status">
           This room is now closed.
          </p>
        ) : null}

        {/* Max Player Limit Popup */}
        {showMaxPlayerPopup && (
          <div className="userpage__popup-overlay" onClick={() => setShowMaxPlayerPopup(false)}>
            <div className="userpage__popup" onClick={(e) => e.stopPropagation()}>
              <div className="userpage__popup-header">
                {/* <h3>Player Limit Reached</h3> */}
                <button 
                  className="userpage__popup-close" 
                  onClick={() => setShowMaxPlayerPopup(false)}
                  aria-label="Close popup"
                >
                  ×
                </button>
              </div>
              <div className="userpage__popup-content">
                <p>Oops, you're late 😊. This room is already full.<br />👉🏻 Start a new game!</p>
              </div>
              <div className="userpage__popup-footer">
                <button 
                  className="userpage__btn userpage__btn--secondary"
                  onClick={() => setShowMaxPlayerPopup(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        <footer className="userpage__footer">
          <button
            type="button"
            className="userpage__btn userpage__btn--play"
            onClick={handlePlay}
            disabled={isPlayDisabled}
            title={
              playerPlayLockedUntilPlayerMode
                ? "Select Player mode to continue"
                : playerModeAutoSelectionPending
                ? "Syncing player mode…"
                : playerModeToggleSelected && !gameModeConfirmed
                ? "Click Player toggle to confirm your selection"
                : undefined
            }
          >
            {busy
              ? "Saving…"
              : playerPlayLockedUntilPlayerMode
                ? "Select Player"
              : playerModeAutoSelectionPending
                ? "Loading…"
                : playerModeToggleSelected && !gameModeConfirmed
                ? "Select Player"
                : "Play"}
          </button>
         
          <button
            type="button"
            className="userpage__btn userpage__btn--secondary"
            onClick={handleCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </footer>
        </div>
      )}
    </div>
  );
}

export default UserPage;
